import { useDispatch, useSelector } from 'react-redux';
import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Routes from 'lib/utils/routes';
import queryString from 'query-string';

// Components
import { AddWorkOrderModal } from 'components/JobTable/AddWorkOrderModal';

// Selectors
import { getMachine } from 'lib/selectors/getMachine';
import { getNeedsForceLogin } from 'lib/selectors/getCanOpenLaborTicket';
import SwipeUpModal, { IconWrapper } from 'components/SwipeUpModal';
import { Loading } from 'components/Loading';
import { P } from 'components/Text';
import useSelectOperator from 'lib/hooks/useSelectOperator';
import { useJobDisplayName } from 'lib/hooks/useJobDisplayName';
import { getWorkOrderOpActionLoading } from 'lib/selectors/getWorkOrderOpActionLoading';
import { getLaborTicketSplitDraft } from 'lib/selectors/getLaborTicketSplitDraft';
import {
  actionCloseSelectWorkOrderOp,
  actionOpenSelectWorkOrderOp,
  actionPatchSelectWorkOrderOpOptions,
  actionSetWorkOrderOpActionLoading,
} from 'lib/actions';
import {
  getSelectWorkOrderOpOpen,
  getSelectWorkOrderOpOptions,
} from 'lib/selectors/getSelectWorkOrderOp';
import { getMachineSchedule } from 'lib/selectors/getMachineSchedule';
import { getCurrentWorkOrderOp } from 'lib/selectors/getCurrentWorkOrderOp';
import { getProgramEvents } from 'lib/selectors/getProgramEvents';
import { Icon, palette } from '@m12s/component-library';
import { DefaultCardBorder } from 'components/MachineHeader/styled';
import { useTranslation } from 'react-i18next';
import { getProductionScheduleEnabled } from 'lib/selectors/getProductionScheduleEnabled';
import { getEnabledBarcodeFields } from 'lib/selectors/getBarcodeFieldConfig';
import { useRequestMachineSchedule } from 'lib/hooks/useRequestMachineSchedule';
import HorizontalScrollContainer from 'components/HorizontalScrollContainer';
import { smartAppend, replaceField, removeField } from 'lib/utils/barcode';
import { throwSuccessToast, throwErrorToast } from 'lib/utils/toast';
import { getActivityTypes } from 'lib/selectors/getActivityTypes';
import { getPendingActivityTypeRef } from 'lib/selectors/getPendingActivityTypeRef';
import { getIsJobLaborTrackingEnabled } from 'lib/selectors/getIsJobLaborTrackingEnabled';
import { getMaterialPickingEnabled } from 'lib/selectors/getMaterialPickingEnabled';
import { ACTIVITY_MODES } from 'lib/constants';
import useStartNewProductionRun from 'components/SwitchActivityTypeReview/useStartNewProductionRun';
import useSelectActivity from 'lib/hooks/useSelectActivity';
import { findMatchingActivities } from 'lib/utils/activityMatching';
import { getSwitchActivityTypesOptions } from 'lib/selectors/getSwitchActivityTypesOptions';
import useBarcodeScanHandler from 'lib/hooks/useBarcodeScanHandler';
import { BARCODE_HANDLER_PRIORITIES } from 'lib/constants/barcodePriorities';
import useRequestJobStartAt from 'lib/hooks/useRequestJobStartAt';
import useRequestPartCountMultiplier from 'lib/hooks/useRequestPartCountMultiplier';

// misc
import { getHasOpenActivitySet } from 'lib/selectors/getHasOpenActivitySet';
import { getIsAPMEnabled } from 'lib/selectors/getIsAPMEnabled';
import {
  postChangeJobForEvent,
  postSplitProgramEvents,
} from 'lib/api/multiJobSelectRest';
import { falTimes } from 'lib/icons';
import { useHistory } from 'react-router-dom';
import {
  useEffectiveSelectJobRouteState,
  useSelectJobFlowContext,
} from 'pages/SelectJob/SelectJobFlowContext';
import {
  findCardWooRefForEvent,
  matchCurrentJobToSearch,
  searchOnProductionScheduleOperations,
  isLiveSplitDestinationPick,
} from './helpers';
import useSelectOperationFlow from './useSelectOperationFlow';
import usePickSplitDestination from './hooks/usePickSplitDestination';
import useSamePartOperationWorkOrderOps from './hooks/useSamePartOperationWorkOrderOps';
import useApmActiveWorkOrderOps from './hooks/useApmActiveWorkOrderOps';
import useCurrentWorkOrderOpRecord from './hooks/useCurrentWorkOrderOpRecord';
import useCreateActivitySetWithWorkOrder from '../useCreateActivitySetWithWorkOrder';
import Header from './Header';
import NextWorkOrderOpCard from './NextWorkOrderOpCard';
import WorkOrderOpGroup from './WorkOrderOpGroup';
import EmptyMessage from './EmptyMessage';

const SearchWorkOrderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.5rem;
`;

const StandardWorkOrderContainer = styled.div`
  ${DefaultCardBorder}
  background-color: ${palette.Grey200};
  border-radius: 0.5rem 0 0 0;
  height: 100%;
  overflow: hidden;
  width: 100%;

  flex: 1; /* takes remaining space */
`;

const VerticalScrollContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-width: max-content;
  height: 100%;
`;

const SearchRootContainer = styled.div`
  display: flex;
  flex-direction: row;
  overflow: auto;
  overflow-y: hidden;
  height: 100%;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
`;

const StandardRootContainer = styled.div`
  display: flex;
  flex-direction: row;
  overflow: auto;
  overflow-y: hidden;
  height: 100%;
`;

const HeaderText = styled.h5`
  font-size: 0.75rem;
  line-height: 0.75rem;
  font-weight: 700;
  color: ${palette.Grey700};
  margin: 0;
`;

const DrawerActionOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10;
  background-color: ${palette.Grey100};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const SelectWorkOrderOpModal = ({
  handleOnClose = () => {},
  nextStep: baseNextStep,
  dispatch,
  workOrderOp = null,
  embeddedInDrawer = false,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const selectJobFlowCtx = useSelectJobFlowContext();
  const effectiveRouteState = useEffectiveSelectJobRouteState(
    history.location.state
  );

  const [manualModalOpen, setManualModalOpen] = useState(false);
  const [newRunModalOpen, setNewRunModalOpen] = useState(false);
  const [selectedField, setSelectedField] = useState(null);

  // Track the last search value that triggered auto-selection to prevent loops
  const lastAutoSelectedSearchRef = useRef(null);
  // Track when a barcode was just scanned (to distinguish from manual edits)
  const barcodeScanOccurredRef = useRef(false);
  const isSubmittingRef = useRef(false);

  const machine = useSelector(getMachine);
  const productionScheduleEnabled = useSelector(getProductionScheduleEnabled);
  const needsForceLogin = useSelector(getNeedsForceLogin);
  const { openSelectOperator, selectOperatorOpen } = useSelectOperator();

  const redirectPath = Routes.machineIdSelectJobPath(machine.id);
  const homePath = Routes.machineIdHomePath(machine.id);
  const hasOpenActivitySet = useSelector(getHasOpenActivitySet);

  const storeDispatch = useDispatch();
  const selectWorkOrderOpOpen = useSelector(getSelectWorkOrderOpOpen);
  const { isSearchMode, search, fromBarcodeScan } = useSelector(
    getSelectWorkOrderOpOptions
  );
  const enabledBarcodeFields = useSelector(getEnabledBarcodeFields);

  // Selectors and hooks needed for handleAutoSelection (matching WorkOrderCard logic)
  const activityTypes = useSelector(getActivityTypes);
  const switchActivityTypes = useSelector(getSwitchActivityTypesOptions);
  const pendingActivityTypeRef = useSelector(getPendingActivityTypeRef);
  const isJobLaborTrackingEnabled = useSelector(getIsJobLaborTrackingEnabled);
  const materialPickingEnabled = useSelector(getMaterialPickingEnabled);
  const { openSelectActivity, selectActivityOpen } = useSelectActivity();
  const startNewProductionRun = useStartNewProductionRun();
  const requestJobStartAt = useRequestJobStartAt();
  const requestPartCountMultiplier = useRequestPartCountMultiplier();
  const isAPMEnabled = useSelector(getIsAPMEnabled);
  const splitDraft = useSelector(getLaborTicketSplitDraft);
  const pickSplitDestination = usePickSplitDestination();
  const workOrderOpActionLoading = useSelector(getWorkOrderOpActionLoading);
  const { getOrderOpDisplayName } = useJobDisplayName();

  const orderOpDisplayName = useMemo(() => {
    return getOrderOpDisplayName(workOrderOp);
  }, [getOrderOpDisplayName, workOrderOp]);

  const setupActivityTypes = activityTypes.filter((type) => {
    return type.activityMode === ACTIVITY_MODES.SETUP;
  });
  const shouldStartAsSetup =
    !isJobLaborTrackingEnabled && setupActivityTypes?.length === 1;

  // Track matched activity type from barcode scan (e.g., scanning "SETUP")
  const [matchedActivityTypeRef, setMatchedActivityTypeRef] = useState(null);

  const InnerContainer = isSearchMode
    ? VerticalScrollContainer
    : HorizontalScrollContainer;

  // Helper functions to prevent code duplication
  const ensureModalClosed = useCallback(() => {
    if (selectWorkOrderOpOpen) {
      storeDispatch(actionCloseSelectWorkOrderOp());
    }
  }, [selectWorkOrderOpOpen, storeDispatch]);

  const ensureModalOpen = useCallback(() => {
    if (!selectWorkOrderOpOpen) {
      storeDispatch(actionOpenSelectWorkOrderOp());
    }
  }, [selectWorkOrderOpOpen, storeDispatch]);

  const changeSearchMode = () => {
    storeDispatch(
      actionPatchSelectWorkOrderOpOptions({
        isSearchMode: !isSearchMode,
      })
    );
  };

  // Handle barcode scan events when on SelectJob page
  const handleBarcodeScan = useCallback(
    (barcode) => {
      throwSuccessToast(`Barcode scanned: ${barcode}`);

      // First, check if the barcode matches an activity type (e.g., "SETUP", "Production")
      const activityMatches = findMatchingActivities(
        barcode,
        switchActivityTypes
      );

      if (activityMatches.length === 1) {
        // Activity type matched! Store it for auto-selection flow
        const matchedActivity = activityMatches[0];
        setMatchedActivityTypeRef(matchedActivity.activityTypeRef);
        throwSuccessToast(`Activity matched: ${matchedActivity.name}`);

        // Don't add to search - just mark that we have a matched activity
        // The auto-selection will use this when a single work order is found
        return;
      }

      if (activityMatches.length > 1) {
        // Multiple activity types matched
        throwErrorToast('Error: Multiple activities found');
        return;
      }

      // No activity match - proceed with work order search logic
      // If modal is not in search mode, switch to it
      if (!isSearchMode) {
        storeDispatch(
          actionPatchSelectWorkOrderOpOptions({
            isSearchMode: true,
          })
        );
      }

      const currentSearch = search || '';
      let updatedSearch;

      // If a field is selected, replace its value
      if (selectedField) {
        updatedSearch = replaceField(
          currentSearch,
          selectedField,
          barcode,
          enabledBarcodeFields
        );
        setSelectedField(null); // Clear selection after replacement
      } else {
        // Otherwise, append to existing search using smart append
        updatedSearch = smartAppend(
          currentSearch,
          barcode,
          enabledBarcodeFields
        );
      }

      // Mark that a barcode scan occurred (for auto-selection logic)
      barcodeScanOccurredRef.current = true;

      // Update search in Redux to trigger filtering
      storeDispatch(
        actionPatchSelectWorkOrderOpOptions({
          isSearchMode: true,
          search: updatedSearch,
          fromBarcodeScan: true, // Mark for potential auto-selection
        })
      );
    },
    [
      storeDispatch,
      isSearchMode,
      search,
      enabledBarcodeFields,
      selectedField,
      switchActivityTypes,
    ]
  );

  // Detect if search came from barcode scan on mount (from Machine.js navigation)
  // This ensures auto-selection works when navigating with an initial barcode scan
  useEffect(() => {
    if (fromBarcodeScan && !lastAutoSelectedSearchRef.current) {
      // Search came from a barcode scan (set by Machine.js or handleBarcodeScan)
      barcodeScanOccurredRef.current = true;
      // Clear the flag so it doesn't persist
      storeDispatch(
        actionPatchSelectWorkOrderOpOptions({
          fromBarcodeScan: false,
        })
      );
    }
  }, [fromBarcodeScan, storeDispatch]); // Run when fromBarcodeScan changes

  // Determine if we should allow single-character scans
  // Only allow minLength: 1 when we already have a workOrderId (subsequent fields can be single digit)
  const hasWorkOrderId = search?.includes('workOrderId:');
  const scannerMinLength = hasWorkOrderId ? 1 : 2;

  // Memoize scanner options to prevent unnecessary re-initialization
  const scannerOptions = useMemo(() => {
    return {
      minLength: scannerMinLength,
    };
  }, [scannerMinLength]);

  // Initialize barcode scanner for SelectJob page (only when modal is open)
  useBarcodeScanHandler('select-work-order', handleBarcodeScan, {
    priority: BARCODE_HANDLER_PRIORITIES.SELECT_WORK_ORDER_MODAL,
    enabled: selectWorkOrderOpOpen,
    scanOptions: scannerOptions,
  });

  // The guards below bounce off this screen when a run is open. A split is the
  // exception: there is always one, and picking a job here starts nothing.
  const isSplitDestinationPick = isLiveSplitDestinationPick(
    effectiveRouteState,
    splitDraft
  );
  const allowsOpenActivitySet =
    !hasOpenActivitySet || isAPMEnabled || isSplitDestinationPick;

  // Only open modal if no activity is already open (APM allows adding jobs while running)
  useEffect(() => {
    if (embeddedInDrawer) return;
    if (allowsOpenActivitySet) {
      ensureModalOpen();
    } else {
      ensureModalClosed();
      history.push(homePath);
    }
  }, [
    embeddedInDrawer,
    allowsOpenActivitySet,
    ensureModalOpen,
    ensureModalClosed,
    history,
    homePath,
  ]);

  // Prevent job overlap: redirect to home if activity is already open (APM allows it)
  const nextStep = useCallback(() => {
    if (allowsOpenActivitySet) {
      baseNextStep();
    } else {
      ensureModalClosed();
      history.push(homePath);
    }
  }, [
    allowsOpenActivitySet,
    baseNextStep,
    ensureModalClosed,
    history,
    homePath,
  ]);

  useEffect(() => {
    if (needsForceLogin && !selectOperatorOpen) {
      openSelectOperator({
        redirectPath,
      });
    }
  }, [needsForceLogin, selectOperatorOpen, openSelectOperator, redirectPath]);

  // Handler for field value changes (manual edit)
  const handleFieldChange = useCallback(
    (fieldName, newValue) => {
      const currentSearch = search || '';
      const updatedSearch = replaceField(
        currentSearch,
        fieldName,
        newValue,
        enabledBarcodeFields
      );

      // Clear barcode scan flag since this is a manual edit
      barcodeScanOccurredRef.current = false;

      storeDispatch(
        actionPatchSelectWorkOrderOpOptions({
          search: updatedSearch,
        })
      );
    },
    [search, enabledBarcodeFields, storeDispatch]
  );

  // Handler for field deletion
  const handleFieldDelete = useCallback(
    (fieldName) => {
      const currentSearch = search || '';
      const updatedSearch = removeField(
        currentSearch,
        fieldName,
        enabledBarcodeFields
      );

      // If deleting the selected field, clear selection
      if (selectedField === fieldName) {
        setSelectedField(null);
      }

      // Clear barcode scan flag since this is a manual deletion
      barcodeScanOccurredRef.current = false;

      storeDispatch(
        actionPatchSelectWorkOrderOpOptions({
          search: updatedSearch,
        })
      );
    },
    [search, enabledBarcodeFields, selectedField, storeDispatch]
  );

  // Initialize useCreateActivitySetWithWorkOrder hook at component level
  // This hook is used when a matched activity type is found during barcode scan
  // Note: workOrderOp is passed at call time to avoid stale closure issues
  const requestCreateActivitySetWithWorkOrder = useCreateActivitySetWithWorkOrder(
    {
      machine,
      workOrderOp: null, // Will be provided at call time
    }
  );

  // Handler for auto-selecting a work order operation when only one result exists
  // This replicates the logic from WorkOrderCard's handleSelectJob → handleStartAndUpdateManual → handleJobSelected
  // PLUS handles matched activity types from barcode scans (e.g., scanning "SETUP")
  const handleAutoSelection = useCallback(
    async (selectedWorkOrderOp) => {
      if (!selectedWorkOrderOp) return;

      // Step 1: Set work order operation in state (same as WorkOrderCard)
      dispatch({
        type: 'SET_WORK_ORDER_OPERATION',
        workOrderOp: selectedWorkOrderOp,
      });

      // Extract the part operation from the work order operation
      const partOperation =
        selectedWorkOrderOp?.matchingPartOperations?.length > 0
          ? selectedWorkOrderOp.matchingPartOperations[0]
          : {
              // if no matchingPartOperations, values are included directly on woop
              ...selectedWorkOrderOp,
            };

      // Destination pick, when a search matches one job: choose, start
      // nothing. Above the silent guard below, so a job with no part operation
      // gets the toast instead of no response.
      if (isSplitDestinationPick) {
        pickSplitDestination(selectedWorkOrderOp);
        return;
      }

      if (!partOperation?.partOperationRef) {
        return;
      }

      // Step 2: Check for query params (like activityTypeRef from Switch Activity flow)
      const queryParams = queryString.parse(history.location.search);
      if (queryParams.activityTypeRef) {
        startNewProductionRun(
          queryParams.activityTypeRef,
          selectedWorkOrderOp.workOrderId,
          selectedWorkOrderOp.workOrderOperationRef
        );
        return;
      }

      // APM machines
      if (isAPMEnabled) {
        if (effectiveRouteState?.isSplitChangeJob) {
          const splitCtx = selectJobFlowCtx?.splitJobContext;
          // Picking a job only CHOOSES the destination — the split review
          // reopens with it and the operator confirms there.
          if (splitCtx && selectJobFlowCtx?.onSplitJobSelected) {
            const partOp =
              selectedWorkOrderOp?.matchingPartOperations?.length > 0
                ? selectedWorkOrderOp.matchingPartOperations[0]
                : selectedWorkOrderOp;
            selectJobFlowCtx.onSplitJobSelected({
              workOrderOperationRef: selectedWorkOrderOp.workOrderOperationRef,
              workOrderId: selectedWorkOrderOp.workOrderId ?? null,
              partNumber: partOp?.partNumber ?? null,
              sequenceNumber:
                partOp?.sequenceNumber ??
                selectedWorkOrderOp.sequenceNumber ??
                null,
              finishQuantity:
                selectedWorkOrderOp.finishQuantity ??
                selectedWorkOrderOp.quantityScheduled ??
                null,
            });
            return;
          }
          if (splitCtx) {
            try {
              await postSplitProgramEvents(
                splitCtx.machineRef,
                splitCtx.splitTime,
                splitCtx.workOrderOperationRef,
                selectedWorkOrderOp.workOrderOperationRef
              );
            } catch {
              throwErrorToast('Failed to change job');
              return;
            }
          }
          if (selectJobFlowCtx?.onApmFlowComplete) {
            selectJobFlowCtx.onApmFlowComplete();
          } else {
            history.push(Routes.machineIdHomePath(machine.id));
          }
          return;
        }

        const eventId = effectiveRouteState?.eventId;

        if (eventId) {
          if (isSubmittingRef.current) return;
          isSubmittingRef.current = true;

          storeDispatch(actionSetWorkOrderOpActionLoading(true));
          try {
            await postChangeJobForEvent(
              machine.machineRef,
              eventId,
              selectedWorkOrderOp.workOrderOperationRef,
              effectiveRouteState?.sourceEventRefs
            );
            throwSuccessToast(
              `Changed to "${selectedWorkOrderOp.workOrderId}"`
            );
            window.dispatchEvent(new CustomEvent('apm:opdash-refresh'));
            if (selectJobFlowCtx?.onApmFlowComplete) {
              selectJobFlowCtx.onApmFlowComplete();
            } else {
              history.push(Routes.machineIdHomePath(machine.id));
            }
          } catch {
            throwErrorToast('Failed to change job');
          } finally {
            isSubmittingRef.current = false;
            storeDispatch(actionSetWorkOrderOpActionLoading(false));
          }
        } else {
          openSelectActivity({
            workOrderOp: selectedWorkOrderOp,
            partOperation,
            isApmStartJob: true,
          });
          if (selectJobFlowCtx?.onApmFlowComplete) {
            selectJobFlowCtx.onApmFlowComplete();
          } else {
            history.push(Routes.machineIdHomePath(machine.id));
          }
        }
        return;
      }

      // Step 3: Set part operation in state
      dispatch({
        type: 'SET_PART_OPERATION',
        partOperation,
      });

      // Step 4: Check if we have a matched activity type from barcode scan.
      // With material picking the operator picks first and then selects the
      // activity, so a scanned activity does not start the job directly.
      if (matchedActivityTypeRef && materialPickingEnabled) {
        setMatchedActivityTypeRef(null);
      } else if (matchedActivityTypeRef) {
        // Activity type was matched (e.g., "SETUP" was scanned)
        // Respect company configuration for job time prompts
        try {
          const startAt = await requestJobStartAt();

          const defaultPartCountMultiplier =
            selectedWorkOrderOp?.quantityPerPart ||
            selectedWorkOrderOp?.quantityPerCycle ||
            partOperation?.quantityPerPart ||
            1;
          const partCountMultiplier = await requestPartCountMultiplier(
            defaultPartCountMultiplier,
            matchedActivityTypeRef
          );

          storeDispatch(actionSetWorkOrderOpActionLoading(true));

          // Pass workOrderOp directly to avoid stale closure issues
          await requestCreateActivitySetWithWorkOrder({
            activityTypeRef: matchedActivityTypeRef,
            partOperation,
            startAt,
            partCountMultiplier,
            workOrderOp: selectedWorkOrderOp,
          });

          // Clear the matched activity type
          setMatchedActivityTypeRef(null);
        } catch (error) {
          // Reset loading state and cleanup
          storeDispatch(actionSetWorkOrderOpActionLoading(false));
          setMatchedActivityTypeRef(null);

          if (error?.code !== 'USER_CANCELLED') {
            throwErrorToast('Failed to start activity');
          }
        }

        return;
      }

      // Step 5: Handle the job selection logic (matching WorkOrderCard's handleJobSelected)
      if (isJobLaborTrackingEnabled) {
        // Material picking (scan-only) runs between WOO selection and activity
        // selection: advance to the pick step, which opens activity on complete.
        if (materialPickingEnabled) {
          nextStep();
        } else if (!selectActivityOpen && machine) {
          // For labor tracking mode, open activity selection modal
          openSelectActivity({
            workOrderOp: selectedWorkOrderOp,
            partOperation,
          });
        }
      } else {
        // For non-labor tracking mode, set loading state if needed and proceed to next step
        // The next step will handle activity type selection or auto-start with setup
        if (shouldStartAsSetup || pendingActivityTypeRef) {
          storeDispatch(actionSetWorkOrderOpActionLoading(true));
        }
        nextStep();
      }
    },
    [
      dispatch,
      nextStep,
      history,
      machine,
      isJobLaborTrackingEnabled,
      shouldStartAsSetup,
      pendingActivityTypeRef,
      selectActivityOpen,
      startNewProductionRun,
      openSelectActivity,
      storeDispatch,
      matchedActivityTypeRef,
      requestJobStartAt,
      requestPartCountMultiplier,
      requestCreateActivitySetWithWorkOrder,
      isAPMEnabled,
      effectiveRouteState,
      selectJobFlowCtx,
      isSplitDestinationPick,
      pickSplitDestination,
      materialPickingEnabled,
    ]
  );

  const skipRequestMachineSchedule = !productionScheduleEnabled || !machine?.id;
  const { loading: machineScheduleLoading } = useRequestMachineSchedule({
    variables: {
      machineId: machine?.id,
    },
    skip: skipRequestMachineSchedule,
  });

  const {
    machineScheduledWorkOrderOps,
    notMachineScheduledWorkOrderOps,
    pageLoading,
    excludeOperations,
    error,
    Components,
  } = useSelectOperationFlow({
    selectedField,
    onFieldSelect: setSelectedField,
    onFieldChange: handleFieldChange,
    onFieldDelete: handleFieldDelete,
    machineScheduleLoading,
  });

  const machineSchedule = useSelector(getMachineSchedule);
  const currentWorkOrderOp = useSelector(getCurrentWorkOrderOp);

  // Change-job and split-change-job lead with other work orders for the SAME
  // part operation as the run being changed; Search then expands to the full
  // list. The run is the active run for both flows (split carries its WOO ref).
  const isChangeJobFlow =
    effectiveRouteState?.eventId != null ||
    !!effectiveRouteState?.isSplitChangeJob;
  // Broader than isChangeJobFlow, which also drives the same-part search and
  // the title. A same-job split needs the running job listed, nothing else.
  const offersRunningJob = isChangeJobFlow || isSplitDestinationPick;
  const changeJobRunWooRef =
    selectJobFlowCtx?.splitJobContext?.workOrderOperationRef ??
    currentWorkOrderOp?.workOrderOperationRef ??
    null;
  const {
    samePartOperationWorkOrderOps,
    orderOperation: changeJobRunPartOp,
    loading: samePartOperationLoading,
  } = useSamePartOperationWorkOrderOps({
    workOrderOperationRef: changeJobRunWooRef,
    skip: !isChangeJobFlow,
  });
  // Jobs on the machine's APM board are offered when changing a job, ahead of
  // other jobs for the same part. The current job keeps its own card. A card
  // is never reassigned to its own job (splitting into the same job is fine).
  const {
    apmActiveWorkOrderOps,
    loading: apmActiveWorkOrderOpsLoading,
  } = useApmActiveWorkOrderOps({
    skip: !isChangeJobFlow,
  });
  const {
    currentWorkOrderOpRecord,
    loading: currentWorkOrderOpRecordLoading,
  } = useCurrentWorkOrderOpRecord({
    workOrderOperationRef: currentWorkOrderOp?.workOrderOperationRef,
    skip: !offersRunningJob,
  });
  const programEvents = useSelector(getProgramEvents);
  const changeJobSourceWooRef = effectiveRouteState?.isSplitChangeJob
    ? null
    : findCardWooRefForEvent(programEvents, effectiveRouteState?.eventId);
  const changeJobExcludeOperations = useMemo(() => {
    return changeJobSourceWooRef == null
      ? excludeOperations
      : [...excludeOperations, changeJobSourceWooRef];
  }, [excludeOperations, changeJobSourceWooRef]);
  const changeJobBoardWorkOrderOps = useMemo(() => {
    const excludedRefs = [
      currentWorkOrderOp?.workOrderOperationRef,
      changeJobSourceWooRef,
    ]
      .filter((ref) => {
        return ref != null;
      })
      .map(String);
    return apmActiveWorkOrderOps.filter((op) => {
      return !excludedRefs.includes(String(op.workOrderOperationRef));
    });
  }, [apmActiveWorkOrderOps, currentWorkOrderOp, changeJobSourceWooRef]);
  const changeJobSamePartWorkOrderOps = useMemo(() => {
    const listedRefs = new Set(
      [
        ...changeJobBoardWorkOrderOps.map((op) => {
          return op.workOrderOperationRef;
        }),
        changeJobSourceWooRef,
      ]
        .filter((ref) => {
          return ref != null;
        })
        .map(String)
    );
    return samePartOperationWorkOrderOps.filter((op) => {
      return !listedRefs.has(String(op.workOrderOperationRef));
    });
  }, [
    samePartOperationWorkOrderOps,
    changeJobBoardWorkOrderOps,
    changeJobSourceWooRef,
  ]);

  const changeJobTitle =
    isChangeJobFlow && changeJobRunPartOp
      ? `Select job for ${changeJobRunPartOp.partName ??
          changeJobRunPartOp.partNumber ??
          ''} Op ${changeJobRunPartOp.sequenceNumber ?? ''}`.trim()
      : null;

  const { SwitchToggle, SearchInput } = Components;

  const nextWorkOrderOp = useMemo(() => {
    if (machineSchedule?.length) {
      const next = searchOnProductionScheduleOperations(
        search,
        [machineSchedule[0]],
        getOrderOpDisplayName
      )[0];
      // The card being reassigned isn't a target.
      if (
        next &&
        String(next.workOrderOperationRef) === String(changeJobSourceWooRef)
      ) {
        return null;
      }
      return next;
    }

    return null;
  }, [machineSchedule, search, getOrderOpDisplayName, changeJobSourceWooRef]);

  const upcomingOperations = useMemo(() => {
    if (machineSchedule?.length) {
      return searchOnProductionScheduleOperations(
        search,
        machineSchedule?.slice(1),
        getOrderOpDisplayName
      );
    }

    return [];
  }, [machineSchedule, search, getOrderOpDisplayName]);

  // Calculate all filtered results across all sources
  const allFilteredResults = useMemo(() => {
    const results = [];
    if (nextWorkOrderOp) results.push(nextWorkOrderOp);
    if (upcomingOperations) results.push(...upcomingOperations);
    if (machineScheduledWorkOrderOps)
      results.push(...machineScheduledWorkOrderOps);
    if (notMachineScheduledWorkOrderOps)
      results.push(...notMachineScheduledWorkOrderOps);
    return results;
  }, [
    nextWorkOrderOp,
    upcomingOperations,
    machineScheduledWorkOrderOps,
    notMachineScheduledWorkOrderOps,
  ]);

  // Extract single result if only one match exists
  // The card being reassigned is never auto-selected as its own target.
  const singleResult = useMemo(() => {
    const selectable = allFilteredResults.filter((op) => {
      return String(op.workOrderOperationRef) !== String(changeJobSourceWooRef);
    });
    return selectable.length === 1 ? selectable[0] : null;
  }, [allFilteredResults, changeJobSourceWooRef]);

  // The currently-running job is a valid target when changing a run's job, but
  // it never appears in the schedule/unscheduled lists (it's active, already
  // consumed off the schedule), so it would be missing from the picker. Inject
  // it as a selectable card. `search` outlives search mode, so it only narrows
  // the card while searching, and not until the job's full record has loaded,
  // so a slow load never hides it.
  const currentJobItem = useMemo(() => {
    const ref = currentWorkOrderOp?.workOrderOperationRef;
    if (!offersRunningJob || ref == null) {
      return null;
    }
    // Reassigning the running card: its own job isn't a target.
    if (String(ref) === String(changeJobSourceWooRef)) {
      return null;
    }
    const alreadyListed = [
      nextWorkOrderOp,
      ...upcomingOperations,
      ...(machineScheduledWorkOrderOps || []),
      ...(notMachineScheduledWorkOrderOps || []),
    ].some((op) => {
      return op && String(op.workOrderOperationRef) === String(ref);
    });
    if (alreadyListed) {
      return null;
    }
    return matchCurrentJobToSearch(
      currentWorkOrderOp,
      isSearchMode && !currentWorkOrderOpRecordLoading ? search : '',
      getOrderOpDisplayName,
      currentWorkOrderOpRecord
    );
  }, [
    offersRunningJob,
    currentWorkOrderOp,
    currentWorkOrderOpRecord,
    currentWorkOrderOpRecordLoading,
    changeJobSourceWooRef,
    nextWorkOrderOp,
    upcomingOperations,
    machineScheduledWorkOrderOps,
    notMachineScheduledWorkOrderOps,
    isSearchMode,
    search,
    getOrderOpDisplayName,
  ]);

  // Auto-select work order operation when search narrows to exactly one result after a barcode scan
  useEffect(() => {
    // Reset auto-selection tracking when search is cleared
    if (!search) {
      lastAutoSelectedSearchRef.current = null;
      barcodeScanOccurredRef.current = false;
      return;
    }

    // Check if conditions are met for auto-selection
    const shouldAutoSelect =
      singleResult && // Exactly one result
      !pageLoading && // Not loading
      !machineScheduleLoading && // Not loading schedule
      search && // Search value exists
      search !== lastAutoSelectedSearchRef.current && // Haven't auto-selected for this search yet
      selectWorkOrderOpOpen && // Modal is open
      barcodeScanOccurredRef.current; // Only auto-select after barcode scans, not manual edits

    if (shouldAutoSelect) {
      // Mark this search value as processed
      lastAutoSelectedSearchRef.current = search;
      barcodeScanOccurredRef.current = false; // Reset flag after processing

      // Show user feedback
      throwSuccessToast('1 result found - automatically selected');

      // Trigger the selection
      handleAutoSelection(singleResult);
    }
  }, [
    singleResult,
    pageLoading,
    machineScheduleLoading,
    search,
    selectWorkOrderOpOpen,
    handleAutoSelection,
  ]);

  // Count only the cards that actually render: the non-search change-job view
  // shows board and same-part jobs; otherwise the schedule and search lists,
  // where the card being reassigned is hidden.
  const isChangeJobListView = isChangeJobFlow && !isSearchMode;
  const hasVisibleJobs = isChangeJobListView
    ? changeJobBoardWorkOrderOps.length > 0 ||
      changeJobSamePartWorkOrderOps.length > 0
    : !!nextWorkOrderOp ||
      [
        ...(upcomingOperations || []),
        ...(machineScheduledWorkOrderOps || []),
        ...(notMachineScheduledWorkOrderOps || []),
      ].some((op) => {
        return (
          String(op.workOrderOperationRef) !== String(changeJobSourceWooRef)
        );
      });
  const boardJobsLoading = isChangeJobListView && apmActiveWorkOrderOpsLoading;
  const showEmptyMessage =
    error ||
    pageLoading ||
    boardJobsLoading ||
    (!currentJobItem && !hasVisibleJobs);

  if (machineScheduleLoading || (needsForceLogin && !selectOperatorOpen)) {
    return <Loading />;
  }

  const WorkOrderContainer = isSearchMode
    ? SearchWorkOrderContainer
    : StandardWorkOrderContainer;

  const RootContainer = isSearchMode
    ? SearchRootContainer
    : StandardRootContainer;

  const headerNode = (
    <Header
      SwitchToggle={SwitchToggle}
      SearchInput={SearchInput}
      changeSearchMode={changeSearchMode}
      title={changeJobTitle}
    />
  );

  const onCloseSelectWorkOrder = () => {
    ensureModalClosed();
    handleOnClose();
  };

  const isChangingJob =
    effectiveRouteState?.eventId || effectiveRouteState?.isSplitChangeJob;

  const drawerLoadingOverlay = workOrderOpActionLoading ? (
    <DrawerActionOverlay aria-busy="true">
      <Loading
        text={
          <>
            {workOrderOp && (
              <P
                style={{
                  fontWeight: '600',
                  fontSize: '1.25rem',
                  marginTop: '1rem',
                }}
              >
                {isChangingJob
                  ? `Changing to "${workOrderOp.workOrderId} | ${orderOpDisplayName}"`
                  : `Starting "${workOrderOp.workOrderId} | ${orderOpDisplayName}"`}
              </P>
            )}
            <P style={{ fontSize: '1rem', marginTop: '.5rem' }}>
              {isChangingJob
                ? 'Please wait while we change the production order'
                : 'Please wait while we start the production order'}
            </P>
          </>
        }
      />
    </DrawerActionOverlay>
  ) : null;

  const detailsNode = (
    <>
      {manualModalOpen && (
        <AddWorkOrderModal
          forEditWorkOrderId
          handleOnClose={() => {
            return setManualModalOpen(false);
          }}
        />
      )}
      {newRunModalOpen && (
        <AddWorkOrderModal
          isStopStartNewRun
          handleOnClose={() => {
            setNewRunModalOpen(false);
          }}
        />
      )}
      {showEmptyMessage ? (
        <EmptyMessage
          loading={pageLoading || samePartOperationLoading || boardJobsLoading}
          error={error}
        />
      ) : (
        <RootContainer>
          {nextWorkOrderOp && !(isChangeJobFlow && !isSearchMode) && (
            // The machine's next scheduled op is unrelated to the run's part
            // operation, so it's hidden in the same-part-op change-job view.
            <NextWorkOrderOpCard
              workOrderOp={nextWorkOrderOp}
              nextStep={nextStep}
              dispatch={dispatch}
              key={`${nextWorkOrderOp.workOrderId}-${nextWorkOrderOp.partNumber}-${nextWorkOrderOp.sequenceNumber}`}
            />
          )}

          <WorkOrderContainer>
            <InnerContainer>
              <WorkOrderOpGroup
                title={
                  <HeaderText>{t('Current job').toUpperCase()}</HeaderText>
                }
                items={currentJobItem ? [currentJobItem] : []}
                nextStep={nextStep}
                dispatch={dispatch}
                excludeOperations={changeJobExcludeOperations}
              />
              {isChangeJobFlow && !isSearchMode ? (
                // Change-job / split-change-job: lead with the jobs on the
                // machine's board, then other work orders for the same part
                // operation. Search expands to the full list.
                <>
                  <WorkOrderOpGroup
                    title={
                      <HeaderText>{t('Scheduled').toUpperCase()}</HeaderText>
                    }
                    items={changeJobBoardWorkOrderOps}
                    nextStep={nextStep}
                    dispatch={dispatch}
                    excludeOperations={changeJobExcludeOperations}
                  />
                  <WorkOrderOpGroup
                    title={
                      <HeaderText>
                        {t('Other jobs for this part').toUpperCase()}
                      </HeaderText>
                    }
                    items={changeJobSamePartWorkOrderOps}
                    nextStep={nextStep}
                    dispatch={dispatch}
                    excludeOperations={changeJobExcludeOperations}
                  />
                </>
              ) : (
                <>
                  <WorkOrderOpGroup
                    title={
                      <HeaderText>{t('Scheduled').toUpperCase()}</HeaderText>
                    }
                    items={upcomingOperations}
                    nextStep={nextStep}
                    dispatch={dispatch}
                    excludeOperations={changeJobExcludeOperations}
                  />
                  <WorkOrderOpGroup
                    title={
                      <HeaderText>{t('Unscheduled').toUpperCase()}</HeaderText>
                    }
                    items={machineScheduledWorkOrderOps}
                    nextStep={nextStep}
                    dispatch={dispatch}
                    excludeOperations={changeJobExcludeOperations}
                  />
                  <WorkOrderOpGroup
                    title={
                      <HeaderText>
                        {t('Not assigned to this machine').toUpperCase()}
                      </HeaderText>
                    }
                    items={notMachineScheduledWorkOrderOps}
                    nextStep={nextStep}
                    dispatch={dispatch}
                    excludeOperations={changeJobExcludeOperations}
                  />
                </>
              )}
            </InnerContainer>
          </WorkOrderContainer>
        </RootContainer>
      )}
    </>
  );

  if (embeddedInDrawer) {
    // Match SwipeUpModal: one header row = toolbar (search / toggle) + close icon — no separate title bar.
    return (
      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {drawerLoadingOverlay}
        <div
          style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '1rem',
            height: '4.5rem',
            color: palette.Grey700,
            backgroundColor: palette.White,
            borderBottom: 'none',
            fontWeight: 500,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>{headerNode}</div>
          <IconWrapper
            type="button"
            aria-label="Close"
            onClick={onCloseSelectWorkOrder}
          >
            <Icon
              size="2rem"
              color={palette.Grey700}
              icon={falTimes}
              cursor="pointer"
            />
          </IconWrapper>
        </div>
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            backgroundColor: isSearchMode ? palette.Grey200 : palette.White,
            padding: 0,
          }}
        >
          {detailsNode}
        </div>
      </div>
    );
  }

  return (
    <SwipeUpModal
      id="select-work-order-op"
      isOpen={selectWorkOrderOpOpen}
      header={headerNode}
      headerStyles={{
        padding: '1rem',
        height: '4.5rem',
        borderBottom: 'none',
      }}
      onClose={onCloseSelectWorkOrder}
      detailsStyles={{
        backgroundColor: isSearchMode ? palette.Grey200 : palette.White,
        padding: 0,
      }}
      details={detailsNode}
    />
  );
};

SelectWorkOrderOpModal.propTypes = {
  nextStep: PropTypes.func,
  dispatch: PropTypes.func,
  handleOnClose: PropTypes.func,
  embeddedInDrawer: PropTypes.bool,
};

export default SelectWorkOrderOpModal;
